/**
 * SanMar-supplier eligibility for the store-product editor's supplier /
 * Artifi fetches (2026-07-10, Jake -- P0 regression fix).
 *
 * The store-product editor fetches SanMar catalog meta (colors + supplier
 * costs) and Artifi coverage / product_views ONLY for products backed by the
 * SanMar integration. For a genuinely non-SanMar product (e.g. supplier
 * "[RICH] Hard Stuff Inc") the SKU is unknown to SanMar/Artifi, so those
 * proxies 404/502 -- console noise with no payoff.
 *
 * WHY supplier_name and NOT is_smart_product:
 *   `is_smart_product` was the original discriminator (PRs #2027/#2029/
 *   #2030/#2031) but it is a BAD signal -- real SanMar CPs (PC54, PC55,
 *   ST650, K100) carry is_smart_product = false/null, so the gate wrongly
 *   classified them non-SanMar and SKIPPED the fetch -> NO colors, NO
 *   supplier costs (live P0 on 8083, hit on store product 58 / PC54).
 *   Gating on the supplier NAME instead fixes that: SanMar-named suppliers
 *   fetch, unknown/unset suppliers fetch (covers PC54's null-supplier case),
 *   and only a NAMED non-SanMar supplier is suppressed.
 *
 * Contract:
 *   - null / undefined / empty / whitespace-only  -> true  (supplier unknown;
 *     fetch and let the proxy resolve -- covers the null-supplier SanMar case)
 *   - name matching /sanmar/i                      -> true  (SanMar supplier)
 *   - any other non-empty name                     -> false (named non-SanMar
 *     supplier; skip the fetch)
 *
 * SCOPE AS OF GH #3312 -- THIS IS AN ARTIFI GATE, NOT A CATALOG GATE
 * ------------------------------------------------------------------
 *
 * The premise this function was written under has EXPIRED for catalog
 * questions. Its own text below predicted the failure: it would "WRONGLY
 * suppress future SNS/PCNA products ... acceptable NOW because no SNS/PCNA
 * products exist yet". S&S products now exist, so the shortcut stopped
 * being acceptable -- `isSanmarSupplierName('S&S Activewear')` is false,
 * and in `ProductEditor.vue` that published a resolved-EMPTY supplierMeta,
 * so S&S products rendered NO colors and NO sizes while the platform had
 * 96 colors and 9 sizes for BELLA+CANVAS 3001.
 *
 * The CATALOG capability question has therefore moved to
 * `hasCatalogIntegration(supplier_key)` in `utils/supplier-entitlement.js`
 * -- keys, not names, and a set that carries both `sanmar` and `ss_activewear`.
 * Do NOT reintroduce this function for a catalog, product-detail, or live
 * inventory decision.
 *
 * ARTIFI MOVED TO A KEYED PREDICATE (2026-10-07)
 * ---------------------------------------------
 *
 * This used to argue the Artifi callers were still correct because "Artifi
 * is a SanMar-only preview service". It is not: Artifi's tenant carries S&S
 * styles too (3001CVC, 3413 render from S&S SKUs), so this regex blanked
 * every S&S preview in the product editor. The editor's Artifi gates -- Stage.vue,
 * ColorFilmstrip.vue, ColorsPanel.vue, SupplierSkuPanel.vue and
 * useStorefrontColorRenders.js -- now ask
 * `artifiPreviewEligible({ supplierKey, supplierName })` in
 * `utils/supplier-entitlement.js`, keyed on the supplier_key ProductEditor
 * resolves. That predicate falls back to THIS function only until the key
 * resolves, which is the one job this function still has there.
 *
 * The store-level decoration previews (GarmentPreview.vue,
 * GarmentRenderLayer.vue) moved too: their placement payload carries
 * `supplier_key` / `supplier_id`. There are no remaining direct callers --
 * this function now answers only the UNRESOLVED case inside that predicate.
 * Do NOT widen this regex; give a surface the key instead.
 *
 * @param {string|null|undefined} supplierName - the product's effective
 *   supplier_name attribute.
 * @returns {boolean} true when the SanMar/Artifi fetch should fire.
 */
export function isSanmarSupplierName(supplierName) {
    if (supplierName == null) return true;
    const trimmed = String(supplierName).trim();
    if (trimmed === "") return true;
    return /sanmar/i.test(trimmed);
}
